import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  History,
  UserX,
  UserCheck,
  Key,
  Trash2,
  Lock,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  deactivate_member: {
    label: 'Deactivated',
    color: 'border-orange-500/50 text-orange-400',
    icon: UserX,
  },
  reactivate_member: {
    label: 'Reactivated',
    color: 'border-green-500/50 text-green-400',
    icon: UserCheck,
  },
  reset_password: {
    label: 'Password Reset',
    color: 'border-yellow-500/50 text-yellow-400',
    icon: Key,
  },
  delete_member: {
    label: 'Deleted',
    color: 'border-red-500/50 text-red-400',
    icon: Trash2,
  },
};

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return d.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const AuditLogPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'security_head';

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/audit-log?limit=200`);
      setEntries(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-4 md:p-6" data-testid="audit-no-access">
        <PageHeader title="Audit Log" subtitle="Access restricted" />
        <Card className="bg-panel border-white/10 rounded-sm mt-6">
          <CardContent className="p-12 text-center space-y-4">
            <Lock className="w-12 h-12 text-red-400 mx-auto" />
            <p className="font-chivo font-bold text-lg text-white">Security Head Only</p>
            <p className="font-inter text-sm text-muted-foreground">
              The audit log is restricted to the Security Head role.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between gap-4">
        <PageHeader
          title="Audit Log"
          subtitle="Recent admin actions (most recent first)"
        />
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          className="border-white/20 rounded-sm font-mono text-xs uppercase"
          data-testid="audit-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="bg-panel border-white/10 rounded-sm">
        <CardHeader className="border-b border-white/10 pb-4">
          <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Activity ({entries.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!loading && entries.length === 0 && (
            <div className="text-center py-12 space-y-2">
              <Shield className="w-12 h-12 text-muted-foreground/30 mx-auto" />
              <p className="font-mono text-xs text-muted-foreground uppercase">
                No admin actions recorded yet
              </p>
            </div>
          )}
          {!loading && entries.length > 0 && (
            <div className="space-y-2">
              {entries.map((entry) => {
                const meta =
                  ACTION_META[entry.action] || {
                    label: entry.action,
                    color: 'border-white/20 text-white',
                    icon: History,
                  };
                const Icon = meta.icon;
                return (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 p-3 bg-surface rounded-sm border border-white/5"
                    data-testid={`audit-entry-${entry.id}`}
                  >
                    <div
                      className={`w-9 h-9 rounded-sm bg-black flex items-center justify-center shrink-0 ${
                        meta.color.split(' ')[1] || ''
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge
                          variant="outline"
                          className={`font-mono text-[10px] uppercase ${meta.color}`}
                        >
                          {meta.label}
                        </Badge>
                        <span className="font-inter text-sm text-white truncate">
                          <strong>{entry.actor_name || 'Unknown'}</strong>
                          {entry.target_name && (
                            <>
                              {' → '}
                              <span className="text-muted-foreground">
                                {entry.target_name}
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground">
                        <span>{formatTime(entry.timestamp)}</span>
                        {entry.target_email && (
                          <span className="truncate">{entry.target_email}</span>
                        )}
                        {entry.actor_role && (
                          <span className="uppercase">
                            by {entry.actor_role.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
